import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

export const useQueryString = () => {
  const { search } = window.location;
  return useMemo(() => new URLSearchParams(search), [search]);
};

/**
 * Reactive variant of `useQueryString` — re-renders when the router
 * location changes (unlike `useQueryString`, which reads `window.location`
 * once). Prefer this in components that need to react to param updates.
 */
export const useRouterQueryString = () => {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
};

type QueryValue = string | number | null | undefined;

/**
 * Returns a setter that merges the given updates into the current query
 * string via `history.replace`. `undefined`/`null` values delete the key;
 * empty strings are preserved (they set `key=` in the URL).
 */
export const useSetQueryParams = () => {
  const { replace } = useHistory();
  const { search } = useLocation();
  return useCallback(
    (updates: Record<string, QueryValue>) => {
      const params = new URLSearchParams(search);
      Object.entries(updates).forEach(([key, value]) => {
        if (value == null) {
          params.delete(key);
        } else {
          params.set(key, String(value));
        }
      });
      replace({ search: params.toString() });
    },
    [replace, search],
  );
};
